import React, { useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid2 as Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AttachMoney,
  TrendingUp,
  Schedule,
  CheckCircle,
  Receipt,
  Download,
  ArrowForward,
  AccountBalance,
  Payment,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  loadFinanceSummary,
  loadPayouts,
} from "../../../../store/actions/ppActions";

const PpFinanceDashboardPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const currentSession = useSelector((state) => state.sessions.currentSession);
  const ppState = useSelector((state) => state[currentSession]?.pp);
  const financeSummary = ppState?.finance?.summary || {};
  const payouts = ppState?.finance?.payouts || [];

  useEffect(() => {
    dispatch(loadFinanceSummary());
    dispatch(loadPayouts());
  }, [dispatch]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Calculate summary stats
  const totalPending = payouts
    .filter((p) => p.status === "pending")
    .reduce((sum, p) => sum + p.ppShare, 0);

  const totalPaid = payouts
    .filter((p) => p.status === "paid")
    .reduce((sum, p) => sum + p.ppShare, 0);

  // Sort a copy to avoid mutating Redux state
  const recentPayouts = [...payouts]
    .sort((a, b) => new Date(b.month) - new Date(a.month))
    .slice(0, 5);

  const quickActions = [
    {
      title: "View Payouts",
      description: "Track your payment history and status",
      icon: <Payment />,
      path: "/pp/finance/payouts",
      color: "primary",
    },
    {
      title: "Financial Statements",
      description: "Download detailed financial reports",
      icon: <Receipt />,
      path: "/pp/finance/statements",
      color: "secondary",
    },
  ];

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography color="primary" variant="h4">
          Finance Dashboard
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={() => navigate("/pp/finance/statements")}
        >
          Download Reports
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="h6" color="text.secondary">
                    Monthly Earnings
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {formatCurrency(financeSummary.monthlyEarnings)}
                  </Typography>
                </Box>
                <AttachMoney sx={{ fontSize: 40, color: "primary.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="h6" color="text.secondary">
                    Pending Payouts
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {formatCurrency(totalPending)}
                  </Typography>
                </Box>
                <Schedule sx={{ fontSize: 40, color: "warning.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="h6" color="text.secondary">
                    Total Paid
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {formatCurrency(totalPaid)}
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: "success.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="h6" color="text.secondary">
                    Avg Monthly
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {formatCurrency(financeSummary.avgMonthlyRevenue)}
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, color: "info.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <List>
                {quickActions.map((action, index) => (
                  <React.Fragment key={index}>
                    <ListItem
                      button
                      onClick={() => navigate(action.path)}
                      sx={{
                        cursor: "pointer",
                        borderRadius: 1,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ color: `${action.color}.main` }}>
                        {action.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={action.title}
                        secondary={action.description}
                      />
                      <ArrowForward color="action" />
                    </ListItem>
                    {index < quickActions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Payouts */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Recent Payouts</Typography>
                <Button
                  size="small"
                  onClick={() => navigate("/pp/finance/payouts")}
                >
                  View All
                </Button>
              </Box>
              {recentPayouts.length === 0 ? (
                <Typography
                  color="text.secondary"
                  align="center"
                  sx={{ py: 2 }}
                >
                  No payouts available
                </Typography>
              ) : (
                <List>
                  {recentPayouts.map((payout, index) => (
                    <React.Fragment key={payout.id}>
                      <ListItem
                        sx={{
                          px: 0,
                          py: 1,
                        }}
                      >
                        <ListItemIcon>
                          <AccountBalance color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={new Date(
                            payout.month + "-01"
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                          })}
                          secondary={
                            payout.paidDate
                              ? `Paid on ${formatDate(payout.paidDate)}`
                              : "Pending payment"
                          }
                        />
                        <Box sx={{ textAlign: "right" }}>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(payout.ppShare)}
                          </Typography>
                          <Chip
                            label={payout.status}
                            size="small"
                            color={
                              payout.status === "paid"
                                ? "success"
                                : payout.status === "pending"
                                  ? "warning"
                                  : "default"
                            }
                          />
                        </Box>
                      </ListItem>
                      {index < recentPayouts.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Financial Overview */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Financial Overview
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: "center",
                  bgcolor: "primary.50",
                  border: 1,
                  borderColor: "primary.200",
                }}
              >
                <Typography variant="h4" color="primary" gutterBottom>
                  {payouts.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Payouts
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: "center",
                  bgcolor: "success.50",
                  border: 1,
                  borderColor: "success.200",
                }}
              >
                <Typography variant="h4" color="success.main" gutterBottom>
                  {payouts.filter((p) => p.status === "paid").length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed Payments
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: "center",
                  bgcolor: "warning.50",
                  border: 1,
                  borderColor: "warning.200",
                }}
              >
                <Typography variant="h4" color="warning.main" gutterBottom>
                  {payouts.filter((p) => p.status === "pending").length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Payments
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: "center",
                  bgcolor: "info.50",
                  border: 1,
                  borderColor: "info.200",
                }}
              >
                <Typography variant="h4" color="info.main" gutterBottom>
                  {formatCurrency(financeSummary.avgMonthlyRevenue)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg Monthly Revenue
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PpFinanceDashboardPage;
